import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { BRAND } from "@/lib/brand";
import { formatFriendlyDate } from "@/lib/dateUtils";
import { toast } from "sonner";
import {
  Loader2,
  Check,
  RotateCcw,
  DollarSign,
  CalendarDays,
  ChevronDown,
  ChevronRight,
} from "lucide-react";

function statusStyle(status) {
  switch (status) {
    case "submitted":
      return { bg: "#f59e0b22", color: "#b45309", label: "Submitted" };
    case "approved":
      return { bg: `${BRAND.teal}22`, color: BRAND.tealDark, label: "Approved" };
    case "returned":
      return { bg: "#ef444422", color: "#b91c1c", label: "Returned" };
    case "draft":
    default:
      return { bg: "#94a3b820", color: "#475569", label: "Draft" };
  }
}

export default function AdminCommissionsCard() {
  const [submitted, setSubmitted] = useState([]);
  const [recent, setRecent] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showHistory, setShowHistory] = useState(false);
  const [actingId, setActingId] = useState(null);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const [pending, all] = await Promise.all([
        api.get("/admin/commissions", { params: { status: "submitted" } }),
        api.get("/admin/commissions"),
      ]);
      setSubmitted(pending.data?.commissions || []);
      // History excludes the still-submitted ones (those are in the review queue).
      setRecent(
        (all.data?.commissions || []).filter((c) => c.status !== "submitted").slice(0, 25)
      );
    } catch (err) {
      setError(err?.response?.data?.detail || "Could not load commissions.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const approve = async (c) => {
    setActingId(c.id);
    try {
      await api.post(`/admin/commissions/${c.id}/approve`);
      toast.success(
        `Approved ${c.user_name || "freelancer"} commission`,
        { description: `Hours added to their ${formatFriendlyDate(c.period_end)} timesheet.` }
      );
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not approve commission.");
    } finally {
      setActingId(null);
    }
  };

  const ret = async (c) => {
    const reason = window.prompt(
      `Send back to ${c.user_name || "freelancer"}? Optional note (visible to them):`,
      ""
    );
    // Cancel from prompt → null. Empty string is OK (means no reason given).
    if (reason === null) return;
    setActingId(c.id);
    try {
      await api.post(`/admin/commissions/${c.id}/return`, {
        reason: reason || null,
      });
      toast.success("Commission returned to draft");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not return commission.");
    } finally {
      setActingId(null);
    }
  };

  return (
    <div
      className="bg-white rounded-2xl border border-slate-200 overflow-hidden"
      data-testid="admin-commissions-card"
    >
      <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <DollarSign size={16} style={{ color: BRAND.teal }} />
          <h3 className="text-sm font-semibold text-slate-800">Commissions</h3>
          {submitted.length > 0 && (
            <span
              className="ml-1 inline-flex items-center justify-center text-[10px] font-bold px-1.5 rounded-full text-white tabular-nums"
              style={{ background: BRAND.purple }}
              data-testid="commissions-pending-count"
            >
              {submitted.length}
            </span>
          )}
        </div>
        <button
          type="button"
          onClick={() => setShowHistory((v) => !v)}
          data-testid="commissions-history-toggle"
          className="text-xs text-slate-500 hover:text-slate-800 inline-flex items-center gap-1"
        >
          {showHistory ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
          {showHistory ? "Hide history" : `History (${recent.length})`}
        </button>
      </div>

      {loading ? (
        <div className="p-8 flex items-center justify-center">
          <Loader2 className="animate-spin text-slate-400" size={18} />
        </div>
      ) : error ? (
        <div className="p-4 text-sm text-red-700 bg-red-50 border-b border-red-200">
          {error}
        </div>
      ) : (
        <>
          {submitted.length === 0 ? (
            <div className="p-6 text-center text-sm text-slate-500" data-testid="commissions-empty-pending">
              No commissions waiting for review.
            </div>
          ) : (
            <ul className="divide-y divide-slate-100" data-testid="commissions-pending-list">
              {submitted.map((c) => (
                <li key={c.id} className="px-5 py-3 flex flex-wrap items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="text-sm font-medium text-slate-800">
                      {c.user_name || "Freelancer"}
                    </div>
                    <div className="mt-0.5 text-xs text-slate-500 inline-flex items-center gap-1">
                      <CalendarDays size={11} />
                      {formatFriendlyDate(c.period_start)} –{" "}
                      {formatFriendlyDate(c.period_end)}
                    </div>
                    {c.notes && (
                      <div className="mt-1 text-xs text-slate-600 italic">"{c.notes}"</div>
                    )}
                  </div>
                  <div className="text-right">
                    <div className="text-xs text-slate-500 tabular-nums">
                      ${Number(c.total_fees).toFixed(2)} fees ·{" "}
                      {Math.round(c.commission_split * 100)}%
                    </div>
                    <div
                      className="text-base font-semibold tabular-nums"
                      style={{ color: BRAND.teal }}
                    >
                      ${Number(c.commission_amount).toFixed(2)}
                    </div>
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    <button
                      type="button"
                      onClick={() => approve(c)}
                      disabled={actingId === c.id}
                      data-testid={`commission-approve-${c.id}`}
                      className="inline-flex items-center gap-1 text-xs font-medium px-2.5 py-1.5 rounded-md border transition disabled:opacity-60"
                      style={{
                        color: BRAND.teal,
                        borderColor: `${BRAND.teal}55`,
                        background: `${BRAND.teal}10`,
                      }}
                    >
                      {actingId === c.id ? (
                        <Loader2 className="animate-spin" size={12} />
                      ) : (
                        <Check size={12} />
                      )}
                      Approve
                    </button>
                    <button
                      type="button"
                      onClick={() => ret(c)}
                      disabled={actingId === c.id}
                      data-testid={`commission-return-${c.id}`}
                      className="inline-flex items-center gap-1 text-xs font-medium px-2.5 py-1.5 rounded-md border border-slate-200 text-slate-600 hover:bg-slate-50 transition disabled:opacity-60"
                    >
                      <RotateCcw size={12} />
                      Return
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          )}

          {showHistory && (
            <div className="border-t border-slate-100 bg-slate-50/40">
              {recent.length === 0 ? (
                <div className="p-4 text-xs text-slate-500 text-center">
                  No history yet.
                </div>
              ) : (
                <ul className="divide-y divide-slate-100" data-testid="commissions-history-list">
                  {recent.map((c) => {
                    const s = statusStyle(c.status);
                    return (
                      <li
                        key={c.id}
                        className="px-5 py-2.5 flex flex-wrap items-center justify-between gap-3 text-xs"
                      >
                        <div className="min-w-0">
                          <span className="text-slate-700 font-medium">
                            {c.user_name || "Freelancer"}
                          </span>
                          <span className="text-slate-500">
                            {" · "}
                            {formatFriendlyDate(c.period_start)} – {formatFriendlyDate(c.period_end)}
                          </span>
                        </div>
                        <div className="flex items-center gap-3">
                          <span
                            className="text-slate-700 tabular-nums"
                            title={`${Number(c.total_fees).toFixed(2)} fees`}
                          >
                            ${Number(c.commission_amount).toFixed(2)}
                            {c.equivalent_hours != null && (
                              <span className="text-slate-400">
                                {" · "}
                                {Number(c.equivalent_hours).toFixed(2)}h
                              </span>
                            )}
                          </span>
                          <span
                            className="inline-flex items-center text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full"
                            style={{ background: s.bg, color: s.color }}
                          >
                            {s.label}
                          </span>
                          {c.status === "approved" && (
                            <button
                              type="button"
                              onClick={() => ret(c)}
                              disabled={actingId === c.id}
                              data-testid={`commission-return-${c.id}`}
                              className="text-[10px] font-medium text-slate-500 hover:text-slate-800 underline"
                              title="Return this commission to draft (also undoes the time entry)"
                            >
                              Undo
                            </button>
                          )}
                        </div>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}
